import {TranslationObject} from '@colonist/i18n'
import {MapSettingEnum, ModeSettingEnum} from '../../../../../shared/GameEnums'
import {BaseMapData} from '../../../../../shared/map_data/BaseMapData'
import {MapDataController} from '../../../../../shared/map_data/MapDataController'
import {RoomClientState} from '../../../../shared/scripts/RoomClientState.ts'
import {createDivIn, createImgIn, createSpanIn, createLinkIn, setElementInnerHTML, createParagraphIn, createDiv} from '../../../../shared/scripts/ui_utils/DomUtils'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables'
import {UIMRoomCardView} from './UIMRoomCardView'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {socket} from '../../../../shared/scripts/EntryShared.ts'
import {clientI18n} from '../../../../shared/scripts/ClientI18n.ts'
import {UIPopupController} from '../../../../shared/scripts/ui_popup/UIPopupController.ts'
import {ColonistMembershipPerkType} from '@colonist/lobby-types'

export class UIMRoomMapController {
    private readonly container: HTMLDivElement

    private lastGamemodeSelected: ModeSettingEnum
    private readonly cardList: {mapData: BaseMapData, element: UIMRoomCardView, isMapAccessible: boolean}[] = []

    isSelectedMapAccessible: boolean = true

    constructor(container: HTMLDivElement, roomState: RoomClientState) {
        this.container = container
        this.lastGamemodeSelected = roomState.gameSetting.modeSetting
        this.createMembershipCTA()
        this.createAllCards(roomState)
    }

    update(roomState: RoomClientState) {
        this.refreshList(roomState)
        for(const card of this.cardList) {
            const isSelected = roomState.gameSetting.mapSetting == card.mapData.mapEnum
            card.element.setSelect(isSelected)
            if(isSelected) this.isSelectedMapAccessible = card.isMapAccessible
        }
    }

    private createMembershipCTA() {
        if(ClientUserState.userState?.items.hasMembership()) return

        const membershipCTAContainer = createDivIn(this.container, 'room_center_become_a_member mobile-room')
        createSpanIn(membershipCTAContainer, '', {key: 'strings:roomPage.getAccessToAllMapsAndExpansions'})
        createLinkIn(membershipCTAContainer, '/store#membership', {key: 'strings:homePage.becomeAMember'}, 'btn_general btn_general_no_margin btn_general_orange btn-general-become-a-member', '', true)
    }

    private createAllCards(roomState: RoomClientState) {
        const compatibleMaps = MapDataController.getMapDataCompatibleWithGameMode(roomState.gameSetting.modeSetting)

        const weeklyFreeMap = compatibleMaps.find((mapData) => UIMRoomMapController.shouldShowWeeklyFreeBanner(mapData))
        if(weeklyFreeMap) {
            const weeklyFreeMapIndex = compatibleMaps.indexOf(weeklyFreeMap)
            const weeklyFreeMapData = compatibleMaps[weeklyFreeMapIndex]
            compatibleMaps.splice(weeklyFreeMapIndex, 1)
            compatibleMaps.splice(1, 0, weeklyFreeMapData)
        }

        for(const mapData of compatibleMaps) {
            if(!mapData.isActive) continue

            const mapTitle = UIMRoomMapController.getMapDisplayText(mapData)
            const clickAction = () => socket.roomClient.setMap(mapData.mapEnum)
            const displayInfoAction = () => UIMRoomMapController.displayMapInfo(mapData)

            const canUserAccessMap = ClientUserState.userState?.items.hasAccessToPremiumMap(mapData)
            const isMapFree = UIMRoomMapController.addFreeAfterMapName(mapData)
            const shouldShowWeeklyFreeBanner = UIMRoomMapController.shouldShowWeeklyFreeBanner(mapData)
            const isCardLocked = !canUserAccessMap && !isMapFree

            const card = new UIMRoomCardView(this.container, mapTitle, mapData.image, isCardLocked, true, clickAction, displayInfoAction)
            if(shouldShowWeeklyFreeBanner) card.createBanner({key: 'strings:roomPage.options.map.weeklyFree'})

            this.cardList.push({mapData: mapData, element: card, isMapAccessible: !isCardLocked})
        }

        this.update(roomState)
    }

    private static displayMapInfo(mapData: BaseMapData) {
        const container = createDiv('room-map-preview-grid vertical')
        createImgIn(container, mapData.image, 'room-item-popup-image room-item-map-popup-image')
        const mapInfoBody = UIMRoomMapController.getMapInfoBody(mapData)
        createParagraphIn(container, {key: 'strings:colonistCommon.utils.#buffer', options: {value: mapInfoBody}}, 'room-item-popup-text mobile')

        const translatedPopupBody: TranslationObject = {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: container.outerHTML,
                interpolation: {escapeValue: false},
            },
        }
        UIPopupController.createSimplePopup(mapData.name, translatedPopupBody)
    }

    private static addFreeAfterMapName(mapData: BaseMapData): boolean {
        const isBaseMap = mapData.mapEnum == MapSettingEnum.Classic4P
        if(isBaseMap) return false

        const isWeeklyFreeMap = mapData.mapEnum == UIUtilsEjsVariables.freeMap
        if(isWeeklyFreeMap) return true

        if(!mapData.isMapPremium()) return true

        return false
    }

    private static getMapInfoBody(mapData: BaseMapData): string {
        switch(mapData.mapEnum) {
            case MapSettingEnum.Classic4P: return clientI18n.t('strings:colonistCommon.gameMaps.classic.4P.description')
            case MapSettingEnum.Classic4PRandom: return clientI18n.tArray('strings:colonistCommon.gameMaps.classic.random4P.description').join('')
            case MapSettingEnum.SS1HeadingForNewShores3P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.headingForNewShores', {count: 3})
            case MapSettingEnum.SS1HeadingForNewShores4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.headingForNewShores', {count: 4})
            case MapSettingEnum.SS1HeadingForNewShores56P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.headingForNewShores', {count: 6})
            case MapSettingEnum.TutorialSS1HeadingForNewShores4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.tutorialHeadingForNewShores4P')
            case MapSettingEnum.SS2FourIslands3P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.fourIslands', {count: 3})
            case MapSettingEnum.SS2FourIslands4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.fourIslands', {count: 4})
            case MapSettingEnum.SS2SixIslands56P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.sixIslands')
            case MapSettingEnum.TutorialSS2FourIslands4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.tutorialFourIslands4P')
            case MapSettingEnum.SS3FogIslands3P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.fogIslands', {count: 3})
            case MapSettingEnum.SS3FogIslands4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.fogIslands', {count: 4})
            case MapSettingEnum.SS3FogIslands56P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.fogIslands', {count: 6})
            case MapSettingEnum.TutorialSS3FogIsland4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.tutorialFogIslands4P')
            case MapSettingEnum.SS4ThroughTheDesert3P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.throughTheDesert', {count: 3})
            case MapSettingEnum.SS4ThroughTheDesert4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.throughTheDesert', {count: 4})
            case MapSettingEnum.SS4ThroughTheDesert56P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.throughTheDesert', {count: 6})
            case MapSettingEnum.TutorialSS4ThroughTheDesert4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.tutorialThroughTheDesert4P')
            case MapSettingEnum.Earth: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.earth.description').join('')
            case MapSettingEnum.EarthSeafarers: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.earth.description').join('')
            case MapSettingEnum.USA: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.usa.description').join('')
            case MapSettingEnum.UK: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.uk.description').join('')
            case MapSettingEnum.UKSeafarers: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.uk.description').join('')
            case MapSettingEnum.Diamond: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.diamond.description').join('')
            case MapSettingEnum.Gear: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.gear.description').join('')
            case MapSettingEnum.Lakes: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.lakes.description').join('')
            case MapSettingEnum.Pond: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.pond.description').join('')
            case MapSettingEnum.Twirl: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.twirl.description').join('')
            case MapSettingEnum.ShuffleBoard: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.shuffleBoard.description').join('')
            case MapSettingEnum.BlackForest: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.blackForest.description').join('')
            case MapSettingEnum.Volcano: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.volcano.description').join('')
            case MapSettingEnum.GoldRush: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.goldRush.description').join('')
            default: return ''
        }
    }

    private static getMapDisplayText(mapData: BaseMapData): TranslationObject {
        switch(mapData.mapEnum) {
            case MapSettingEnum.Classic56P: return {key: 'strings:colonistCommon.gameMaps.classic.56P.abbreviation'}
            case MapSettingEnum.Classic78P: return {key: 'strings:colonistCommon.gameMaps.classic.78P.abbreviation'}
            default: return mapData.name
        }
    }

    private static shouldShowWeeklyFreeBanner(mapData: BaseMapData): boolean {
        if(ClientUserState.userState?.items.isPerkAvailable(ColonistMembershipPerkType.AllMaps)) return false
        if(mapData.mapEnum == UIUtilsEjsVariables.freeMap) return true
        return false
    }

    private refreshList(roomState: RoomClientState) {
        if(this.lastGamemodeSelected == roomState.gameSetting.modeSetting) return
        this.lastGamemodeSelected = roomState.gameSetting.modeSetting
        setElementInnerHTML(this.container, {key: 'strings:colonistCommon.utils.emptyString'})
        this.cardList.length = 0
        this.createAllCards(roomState)
    }
}
